import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Alert, Box, Button, LinearProgress, Stack, Typography} from "@mui/material"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined"
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined"
import {useProjectData} from "../../hooks/useProjectData"
import {ImportJobRequests} from "../../api/requests/ImportJobRequests"
import {TransformJobRequests} from "../../api/requests/TransformJobRequests"
import {TransformRequests} from "../../api/requests/TransformRequests"
import {ImportJobJson} from "../../api/json/ImportJobJson"
import TransformJobJson from "../../api/json/TransformJobJson"
import RowJson from "../../api/json/RowJson"

type TransformJobWithContext = TransformJobJson & { transformName: string, transformId: string }

interface ActiveTask {
    kind: 'import' | 'transform'
    id: string
    label: string
    total: number | null
    done: number
    status: 'pending' | 'running' | 'completed' | 'failed' | 'canceled'
    destinationFieldReference?: string
    failedReason?: string | null
    transformId?: string
}

interface ActiveTasksStripProps {
    /** Invoked whenever the strip observes progress on an active task that warrants a dataset refetch. */
    onDatasetShouldRefresh: () => void
    /** Current page's visible rows, used for the saturation gate. */
    currentPageRows?: RowJson[]
    /** Called when the user clicks a transform task row to open its edit modal. */
    onTransformClick?: (transformId: string) => void
    /**
     * Optimistically push a just-started job into local state so it shows up
     * immediately, without waiting for the server to flip it to active.
     * TransformJobStarter runs @Async, so the POST response returns with
     * started=null and the job won't appear in the active-only list poll for
     * up to a second.
     */
    optimisticTransformJob?: {
        key: number
        job: TransformJobJson
        transformId: string
        transformName: string
    }
}

const POLL_INTERVAL_ACTIVE_MS = 1000
const POLL_INTERVAL_IDLE_MS = 10000
const REFRESH_THROTTLE_MS = 3000
const AUTO_DISMISS_MS = 3000

function importJobToTask(job: ImportJobJson): ActiveTask {
    const done = job.processedCount ?? 0
    const total = job.totalRowCount ?? null
    let status: ActiveTask['status'] = 'running'
    if (job.failed) status = 'failed'
    else if (job.completed) status = 'completed'
    return {
        kind: 'import',
        id: job.id,
        label: 'Importing rows',
        total,
        done,
        status,
    }
}

function transformJobToTask(job: TransformJobWithContext): ActiveTask {
    let status: ActiveTask['status'] = 'running'
    if (job.canceled) status = 'canceled'
    else if (job.completed) status = 'completed'
    else if (!job.started) status = 'pending'
    return {
        kind: 'transform',
        id: job.id,
        label: `Transform · ${job.transformName}`,
        total: job.totalTasks ?? null,
        done: (job.succeededCount ?? 0) + (job.failedCount ?? 0),
        status,
        destinationFieldReference: job.destination.reference,
        transformId: job.transformId,
    }
}

function ActiveTasksStrip({onDatasetShouldRefresh, currentPageRows, onTransformClick, optimisticTransformJob}: ActiveTasksStripProps) {
    const {id: projectId, project} = useProjectData()

    const [importJobs, setImportJobs] = useState<ImportJobJson[]>(project?.activeImportJobs ?? [])
    const [transformJobs, setTransformJobs] = useState<TransformJobWithContext[]>([])
    const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())

    // Track previous progress counts per active task so we can detect increases.
    const prevCountsRef = useRef<Map<string, number>>(new Map())
    const lastTickAtRef = useRef<number>(0)
    const currentPageRowsRef = useRef<RowJson[] | undefined>(currentPageRows)
    useEffect(() => {
        currentPageRowsRef.current = currentPageRows
    }, [currentPageRows])

    // Track timers we've already scheduled for auto-dismissal, so we schedule at most one per id.
    const pendingDismissalsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

    // Mirror transformJobs in a ref so pollOnce can diff against the latest state without re-creating.
    const transformJobsRef = useRef<TransformJobWithContext[]>([])
    useEffect(() => {
        transformJobsRef.current = transformJobs
    }, [transformJobs])

    // IDs we've already fetched terminal state for, so we don't re-request on every poll.
    const terminalFetchedRef = useRef<Set<string>>(new Set())

    // Cancel any pending dismissal timers when the component unmounts.
    useEffect(() => {
        return () => {
            pendingDismissalsRef.current.forEach(t => clearTimeout(t))
            pendingDismissalsRef.current.clear()
        }
    }, [])

    // Merge poll result into state, preserving cards for briefly-completed tasks (so they can fade out).
    const mergeImportJobs = useCallback((incoming: ImportJobJson[]) => {
        setImportJobs(prev => {
            const byId: Record<string, ImportJobJson> = {}
            prev.forEach(j => byId[j.id] = j)
            incoming.forEach(j => byId[j.id] = j)
            return Object.values(byId)
        })
    }, [])

    const mergeTransformJobs = useCallback((incoming: TransformJobWithContext[]) => {
        setTransformJobs(prev => {
            const byId: Record<string, TransformJobWithContext> = {}
            prev.forEach(j => byId[j.id] = j)
            // Overlay incoming onto prev, but preserve transformName/transformId from prev
            // if the incoming entry lacks them (it shouldn't, but defensively).
            incoming.forEach(j => {
                byId[j.id] = {...byId[j.id], ...j}
            })
            return Object.values(byId)
        })
    }, [])

    // Patch a single transform job in local state (used for optimistic updates + terminal fetches).
    const patchTransformJob = useCallback((id: string, patch: Partial<TransformJobWithContext>) => {
        setTransformJobs(prev => prev.map(j => j.id === id ? {...j, ...patch} : j))
    }, [])

    // Compose the current task list for rendering + activity checks.
    const tasks: ActiveTask[] = [
        ...importJobs
            .filter(j => !dismissedIds.has(j.id))
            .map(importJobToTask),
        ...transformJobs
            .filter(j => !dismissedIds.has(j.id))
            .map(transformJobToTask),
    ]

    const anyActive = tasks.some(t => t.status === 'running' || t.status === 'pending')

    const pollOnce = useCallback(async () => {
        if (!projectId) return
        try {
            const [importsPage, transformsPage] = await Promise.all([
                ImportJobRequests.list(projectId, true),
                TransformRequests.list(projectId, 0),
            ])

            mergeImportJobs(importsPage.content)

            const activeTransformJobs: TransformJobWithContext[] = []
            transformsPage.content.forEach(transform => {
                transform.transformJobs
                    .filter(j => j.active)
                    .forEach(j => activeTransformJobs.push({
                        ...j,
                        transformName: transform.name,
                        transformId: transform.id,
                    }))
            })

            // Detect jobs we were tracking that are no longer in the active list:
            // those just finished (or were canceled). The server's "active" filter hides
            // them from subsequent polls, so we have to fetch their terminal state directly
            // to get the right `completed` / `canceled` flags for the card's status.
            //
            // Guard: a freshly-started job exists in local state with started=null for a
            // brief moment (because TransformJobStarter runs @Async). Don't treat those as
            // dropped — wait for them to show up in incoming once the async starter fires.
            const incomingIds = new Set(activeTransformJobs.map(j => j.id))
            const droppedJobs = transformJobsRef.current.filter(p =>
                !incomingIds.has(p.id)
                && !!p.started
                && !p.completed
                && !p.canceled
                && !terminalFetchedRef.current.has(p.id)
            )

            mergeTransformJobs(activeTransformJobs)

            droppedJobs.forEach(dropped => {
                terminalFetchedRef.current.add(dropped.id)
                TransformJobRequests.get(projectId, dropped.id)
                    .then(finalJob => {
                        patchTransformJob(dropped.id, finalJob)
                    })
                    .catch(() => {
                        // If the fetch fails, at least mark it completed locally so it
                        // can fade out instead of spinning forever.
                        patchTransformJob(dropped.id, {
                            completed: new Date().toISOString(),
                        } as Partial<TransformJobWithContext>)
                    })
            })
        } catch {
            // Silent failure; next tick retries.
        }
    }, [projectId, mergeImportJobs, mergeTransformJobs, patchTransformJob])

    // Poll loop.
    useEffect(() => {
        if (!projectId) return
        pollOnce()
        const interval = setInterval(() => {
            pollOnce()
        }, anyActive ? POLL_INTERVAL_ACTIVE_MS : POLL_INTERVAL_IDLE_MS)

        return () => {
            clearInterval(interval)
        }
        // Intentionally re-run when anyActive toggles so the cadence follows it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, anyActive])

    // Push an optimistic just-started job directly into local state. The POST response returns
    // before TransformJobStarter's async call sets `started`, so this job wouldn't show up in
    // the server's active-only list for a moment — we insert it locally so the user sees the
    // task immediately, and subsequent polls will overwrite it with the real server state.
    useEffect(() => {
        if (!optimisticTransformJob) return
        const {job, transformId, transformName} = optimisticTransformJob
        setTransformJobs(prev => {
            if (prev.some(p => p.id === job.id)) return prev
            return [...prev, {...job, transformId, transformName}]
        })
        // Fire a poll right away too, in case the async starter has already run.
        pollOnce()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optimisticTransformJob?.key])

    // Detect progress + trigger throttled, gated dataset refetches.
    useEffect(() => {
        if (tasks.length === 0) return

        const prev = prevCountsRef.current
        let anyProgress = false
        let anyCompletedTransition = false

        tasks.forEach(task => {
            const prevDone = prev.get(task.id) ?? -1
            if (prevDone !== task.done) {
                if (prevDone >= 0 && task.done > prevDone) anyProgress = true
                prev.set(task.id, task.done)
            }
            // Capture a finished-transition (for the final un-throttled tick)
            const isFinished = task.status === 'completed'
                || task.status === 'canceled'
                || task.status === 'failed'
            if (isFinished && prev.get(`${task.id}:finishedFired`) !== 1) {
                anyCompletedTransition = true
                prev.set(`${task.id}:finishedFired`, 1)
            }
        })

        const now = Date.now()

        // Throttle gate: no more than 1 tick per REFRESH_THROTTLE_MS unless it's a completion transition.
        if (anyCompletedTransition) {
            lastTickAtRef.current = now
            onDatasetShouldRefresh()
            return
        }

        if (!anyProgress) return
        if (now - lastTickAtRef.current < REFRESH_THROTTLE_MS) return

        // Page-saturation gate: skip if every row on the current page already has values for every
        // active transform job's destination column.
        const activeDestinationRefs = tasks
            .filter(t => t.status === 'running' && t.destinationFieldReference)
            .map(t => t.destinationFieldReference!)

        const rows = currentPageRowsRef.current
        if (activeDestinationRefs.length > 0 && rows && rows.length > 0) {
            const allSaturated = activeDestinationRefs.every(ref =>
                rows.every(row => {
                    const cell = row.cells.find(c => c.column.reference === ref)
                    return !!cell && cell.value !== ""
                })
            )
            if (allSaturated) return
        }

        lastTickAtRef.current = now
        onDatasetShouldRefresh()
    }, [tasks, onDatasetShouldRefresh])

    // Schedule auto-dismissal exactly once per terminal task.
    // Bug fix: the previous version re-scheduled on every render because tasks was a new array each time,
    // and the cleanup cancelled the timer before it fired. Now we track pending timers in a ref and only
    // schedule once per id.
    useEffect(() => {
        tasks.forEach(task => {
            if (task.status === 'running' || task.status === 'pending') return
            if (dismissedIds.has(task.id)) return
            if (pendingDismissalsRef.current.has(task.id)) return

            const timer = setTimeout(() => {
                pendingDismissalsRef.current.delete(task.id)
                setDismissedIds(prev => {
                    const next = new Set(prev)
                    next.add(task.id)
                    return next
                })
            }, AUTO_DISMISS_MS)
            pendingDismissalsRef.current.set(task.id, timer)
        })
    }, [tasks, dismissedIds])

    const handleCancel = async (task: ActiveTask) => {
        if (!projectId) return
        if (task.kind === 'transform') {
            // Optimistic update: flip status to canceled immediately so the Cancel button
            // disappears and the row reflects the action without waiting for the next poll.
            // Also skip the terminal-fetch path for this id since we know what it is.
            terminalFetchedRef.current.add(task.id)
            patchTransformJob(task.id, {
                canceled: new Date().toISOString(),
            } as Partial<TransformJobWithContext>)

            try {
                const updated = await TransformJobRequests.cancel(projectId, task.id)
                // Reconcile with server truth.
                patchTransformJob(task.id, {
                    ...updated,
                    transformName: task.label.replace(/^Transform · /, ""),
                    transformId: task.transformId ?? "",
                } as Partial<TransformJobWithContext>)
            } catch {
                // ignore — optimistic state stays
            }
        }
        // Imports don't currently support cancel; no-op.
    }

    if (tasks.length === 0) {
        return <></>
    }

    return (
        <Box sx={{
            mt: 2,
            mb: 3,
            backgroundColor: '#fff',
            border: '1px solid #ececec',
            borderRadius: 1,
        }}>
            <Stack divider={<Box sx={{borderBottom: '1px solid #f0f0f0'}}/>}>
                {tasks.map(task => {
                    const progress = task.total && task.total > 0 ? (task.done / task.total) * 100 : 0
                    const isRunning = task.status === 'running'
                    const isPending = task.status === 'pending'
                    const isInFlight = isRunning || isPending
                    const canCancel = isInFlight && task.kind === 'transform'
                    const canClickRow = task.kind === 'transform' && !!task.transformId && !!onTransformClick
                    return (
                        <Box key={task.id}
                             onClick={canClickRow ? () => onTransformClick!(task.transformId!) : undefined}
                             sx={{
                                 px: 2,
                                 py: 1.25,
                                 cursor: canClickRow ? 'pointer' : 'default',
                                 '&:hover': canClickRow ? {backgroundColor: '#fafafa'} : undefined,
                             }}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Box sx={{color: isInFlight ? 'text.secondary' : 'success.main', display: 'flex'}}>
                                    {task.kind === 'import' && <FileUploadOutlinedIcon fontSize="small"/>}
                                    {task.kind === 'transform' && isInFlight &&
                                        <AutoAwesomeOutlinedIcon fontSize="small"/>
                                    }
                                    {task.kind === 'transform' && !isInFlight &&
                                        <CheckCircleOutlineIcon fontSize="small"/>
                                    }
                                </Box>
                                <Box sx={{flex: 1, minWidth: 0}}>
                                    <Stack direction="row" alignItems="baseline" spacing={1}>
                                        <Typography variant="body2" sx={{fontWeight: 500}} noWrap>
                                            {task.label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {task.status === 'pending' && 'starting…'}
                                            {task.status === 'running' && task.total != null &&
                                                `${task.done} / ${task.total}`}
                                            {task.status === 'completed' && `done · ${task.done} rows updated`}
                                            {task.status === 'canceled' && 'canceled'}
                                            {task.status === 'failed' && (task.failedReason ?? 'failed')}
                                        </Typography>
                                    </Stack>
                                    {isPending &&
                                        <LinearProgress variant="indeterminate"
                                                        sx={{mt: 0.5, height: 4, borderRadius: 2}}/>
                                    }
                                    {isRunning && task.total != null &&
                                        <LinearProgress variant="determinate"
                                                        value={progress}
                                                        sx={{mt: 0.5, height: 4, borderRadius: 2}}/>
                                    }
                                </Box>
                                {canCancel &&
                                    <Button size="small"
                                            color="secondary"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleCancel(task)
                                            }}>
                                        Cancel
                                    </Button>
                                }
                            </Stack>
                            {task.status === 'failed' && task.failedReason &&
                                <Alert severity="error" sx={{mt: 1}}>{task.failedReason}</Alert>
                            }
                        </Box>
                    )
                })}
            </Stack>
        </Box>
    )
}

export default ActiveTasksStrip
